<template>
  <div class="notification-center">
    <!-- 通知徽章 -->
    <el-badge 
      :value="displayUnreadCount" 
      :hidden="unreadCount === 0" 
      :max="99"
      class="notification-badge"
    >
      <el-button 
        type="text"
        icon="iconfont guoran-a-24_01" 
        @click="toggleNotificationPanel"
        class="notification-trigger"
        :title="unreadCount > 0 ? `有 ${unreadCount} 条未读通知` : '通知中心'"
      />
    </el-badge>

    <!-- 通知面板 -->
    <el-drawer
      :visible="showNotificationPanel"
      @close="showNotificationPanel = false"
      title="通知中心"
      direction="rtl"
      size="600px"
      :before-close="handleClose"
      :z-index="2000"
    >
      <div class="notification-panel">
        <div class="notification-tabs-header">
          <el-tabs v-model="activeTab" @tab-click="handleTabClick">
            <!-- 通知标签页 -->
            <el-tab-pane label="通知" name="notification">
              <template #label>
                <span>
                  通知
                  <el-badge 
                    v-if="unreadCount > 0" 
                    :value="unreadCount" 
                    class="tab-badge"
                  />
                </span>
              </template>
            </el-tab-pane>
            
            <!-- 待办标签页 -->
            <el-tab-pane label="待办" name="todo">
            </el-tab-pane>
          </el-tabs>
          
          <!-- 右侧操作按钮 -->
          <div class="tabs-actions">
            <el-button 
              v-if="activeTab === 'notification'"
              type="text" 
              size="small" 
              @click="handleMarkAllAsRead"
              :disabled="unreadCount === 0"
            >
              全部标记为已读
            </el-button>
          </div>
        </div>
        
        <!-- 内容区域 -->
        <div class="tabs-content">
          <NotificationList 
            v-if="activeTab === 'notification'"
            ref="notificationList"
            :notifications="filteredNotifications"
            :loading="loading"
            :has-more="hasMoreNotifications"
            :total="pagination.total"
            :target-notification-id="targetNotificationId"
            @mark-as-read="handleMarkAsRead"
            @delete="handleDeleteNotification"
            @load-more="loadMoreNotifications"
          />
          
          <TodoList 
            v-if="activeTab === 'todo'"
            :todos="todos"
            :loading="loading"
            :has-more="hasMoreTodos"
            :total="pagination.total"
            @handle-todo="handleTodoItem"
            @load-more="loadMoreTodos"
          />
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import NotificationList from './NotificationList.vue'
import TodoList from './TodoList.vue'

export default {
  name: 'NotificationCenter',
  components: {
    NotificationList,
    TodoList
  },
  data() {
    return {
      showNotificationPanel: false,
      activeTab: 'notification',
      targetNotificationId: null
    }
  },
  computed: {
    ...mapState('notification', [
      'notifications', 
      'todos', 
      'unreadCount', 
      'loading', 
      'pagination'
    ]),
    ...mapState('notification', {
      websocketConnected: state => state.websocketConnected,
      currentNotificationUserId: state => state.currentUserId,
      currentWsBase: state => state.websocketBaseUrl,
      notificationSwitcher: state => state.notificationSwitcher
    }),
    ...mapGetters('notification', ['filteredNotifications']),
    
    hasMoreNotifications() {
      return this.notifications.length < this.pagination.total
    },
    
    hasMoreTodos() {
      return this.todos.length < this.pagination.total
    },
    
    displayUnreadCount() {
      // 确保unreadCount是数字
      const count = typeof this.unreadCount === 'number' ? this.unreadCount : 0
      if (count === 0) return 0
      if (count > 99) return '99+'
      return count
    }
  },
  mounted() {
    this.ensureConnection()
    
    // 监听打开通知中心的事件
    if (typeof window !== 'undefined') {
      window.addEventListener('notification:open-center', this.handleOpenNotificationCenter)
      // 监听关闭通知中心的事件（用于进入其他大页面前先关闭抽屉）
      window.addEventListener('notification:close-center', this.handleCloseCenter)
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('notification:open-center', this.handleOpenNotificationCenter)
      window.removeEventListener('notification:close-center', this.handleCloseCenter)
    }
  },
  methods: {
    ...mapActions('notification', [
      'loadNotifications',
      'loadMoreNotifications',
      'loadTodos',
      'loadMoreTodos',
      'markAsRead',
      'markAllAsRead',
      'handleTodo',
      'deleteNotification',
      'searchNotifications',
      'setFilters',
      'reconnect'
    ]),
    
    async toggleNotificationPanel() {
      if (!this.websocketConnected) {
        this.ensureConnection()
      }
      
      const wasClosed = !this.showNotificationPanel
      this.showNotificationPanel = !this.showNotificationPanel
      
      // 如果面板从关闭变为打开，刷新通知列表以确保显示最新数据
      if (wasClosed && this.showNotificationPanel && this.activeTab === 'notification') {
        try {
          await this.loadNotifications()
          // 刷新列表后，触发所有通知项重新检查内容高度
          this.$nextTick(() => {
            setTimeout(() => {
              if (this.$refs.notificationList && typeof this.$refs.notificationList.checkAllNotificationHeights === 'function') {
                this.$refs.notificationList.checkAllNotificationHeights()
              }
            }, 300)
          })
        } catch (error) {
          console.error('[NotificationCenter] 刷新通知列表失败:', error)
        }
      }
    },

    async handleOpenNotificationCenter(event) {
      // console.log('[NotificationCenter] 接收到打开事件:', event)
      const notificationId = event?.detail?.notificationId
      // console.log('[NotificationCenter] 通知ID:', notificationId)
      
      if (notificationId) {
        this.targetNotificationId = notificationId
        this.activeTab = 'notification'
        this.showNotificationPanel = true
        
        // 先刷新通知列表，确保包含最新的通知
        try {
          await this.loadNotifications()
        } catch (error) {
          console.error('[NotificationCenter] 刷新通知列表失败:', error)
        }
        
        // console.log('[NotificationCenter] 已设置面板打开，targetId:', this.targetNotificationId)
        
        // 延迟执行滚动，确保面板已经打开且列表已加载
        this.$nextTick(() => {
          // console.log('[NotificationCenter] nextTick执行')
          setTimeout(() => {
            // console.log('[NotificationCenter] 开始滚动定位，refs:', this.$refs)
            if (this.$refs.notificationList) {
              // console.log('[NotificationCenter] 调用scrollToNotification')
              this.$refs.notificationList.scrollToNotification(notificationId)
            } else {
              // console.warn('[NotificationCenter] notificationList ref不存在')
            }
          }, 300)
        })
      } else {
        // console.warn('[NotificationCenter] notificationId为空')
      }
    },

    handleCloseCenter() {
      this.showNotificationPanel = false
    },

    ensureConnection() {
      if (this.websocketConnected) {
        return
      }

      if (this.notificationSwitcher && this.notificationSwitcher.webSocketManager) {
        const manager = this.notificationSwitcher.webSocketManager
        const status = manager.getStatus ? manager.getStatus() : null
        const readyState = status && typeof status.readyState === 'number'
          ? status.readyState
          : (manager.websocket && typeof manager.websocket.readyState === 'number'
            ? manager.websocket.readyState
            : null)

        const wsEnum = typeof WebSocket !== 'undefined' ? WebSocket : null
        const connectingState = wsEnum ? wsEnum.CONNECTING : 0
        const openState = wsEnum ? wsEnum.OPEN : 1

        if (readyState === connectingState || readyState === openState || manager.isConnected) {
          return
        }
      }

      const payload = {}
      if (this.currentNotificationUserId) {
        payload.userId = this.currentNotificationUserId
      }
      if (this.currentWsBase) {
        payload.baseUrl = this.currentWsBase
      }

      this.$store.dispatch('notification/initNotificationCenter', payload).catch((error) => {
        console.warn('初始化通知中心遇到问题，将静默重连:', error)
      })
    },
    
    handleTabClick(tab) {
      if (tab.name === 'notification') {
        this.loadNotifications()
      } else if (tab.name === 'todo') {
        this.loadTodos()
      }
    },
    
    handleMarkAsRead(notificationId) {
      this.markAsRead(notificationId)
    },
    
    handleMarkAllAsRead() {
      this.markAllAsRead()
      this.$message.success({
        message: '已标记所有通知为已读',
        duration: 5000
      })
    },
    
    handleTodoItem({ todoId, action, data }) {
      this.handleTodo({ todoId, action, data })
    },
    
    handleDeleteNotification(notificationId) {
      this.$confirm('确定要删除这条通知吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.deleteNotification(notificationId)
        this.$message.success({
          message: '通知已删除',
          duration: 5000
        })
      }).catch(() => {
        // 用户取消
      })
    },
    
    handleSearch(keyword) {
      this.searchNotifications(keyword)
    },
    
    handleFilter(filters) {
      this.setFilters(filters)
    },
    
    handleClose() {
      this.showNotificationPanel = false
    },
    
    reconnect() {
      this.$store.dispatch('notification/reconnect')
      this.$message.info('正在尝试重连...')
    },

    checkConnectionStatus() {
      const websocketManager = this.$store.state.notification.websocketManager
      if (websocketManager) {
        console.table(websocketManager.getStatus())
      }
    },
    
    addMockNotification() {
      const mockNotification = {
        id: Date.now(),
        title: '测试通知',
        content: `这是一个测试通知，创建时间：${new Date().toLocaleString()}`,
        type: 'AUTOMATION_NOTIFICATION',
        read: false,
        createTime: new Date().toISOString()
      }
      
      this.$store.commit('notification/ADD_NOTIFICATION', mockNotification)
      this.$message.success({
        message: '已添加测试通知',
        duration: 5000
      })
    },
    
    testWebSocketConnection() {
      const userId = this.$store.state.user?.userId || this.$store.state.user?.id
      const testUrl = `ws://master.test.askbot.cn:30202/ws/notification?userId=${userId}`
      
      console.log('测试WebSocket连接:', testUrl)
      
      try {
        const testWebSocket = new WebSocket(testUrl)
        
        testWebSocket.onopen = () => {
          console.log('WebSocket测试连接成功!')
          this.$message.success({
            message: 'WebSocket连接测试成功!',
            duration: 5000
          })
          testWebSocket.close()
        }
        
        testWebSocket.onerror = (error) => {
          console.error('WebSocket测试连接失败:', error)
          this.$message.error('WebSocket连接测试失败，请检查服务器状态')
          testWebSocket.close()
        }
        
        // 5秒后自动关闭测试连接
        setTimeout(() => {
          if (testWebSocket.readyState === WebSocket.OPEN) {
            testWebSocket.close()
          }
        }, 5000)
        
      } catch (error) {
        console.error('创建WebSocket连接失败:', error)
        this.$message.error('创建WebSocket连接失败: ' + error.message)
      }
    }
  }
}
</script>

<style lang="less" scoped>
.notification-center {
  position: relative;
}

.notification-badge {
  position: relative;
}

/* 使用深度选择器确保图标大小生效 */
/deep/ .notification-badge .iconfont {
  font-size: 24px !important;
}

/deep/ .notification-trigger .iconfont {
  font-size: 24px !important;
}

/deep/ .notification-trigger i.iconfont {
  font-size: 24px !important;
}

/deep/ .notification-badge i.iconfont {
  font-size: 24px !important;
}

.notification-trigger {
  color: #606266;
  padding: 8px;
  border: none;
  background: none;
  border-radius: 4px;
  transition: all 0.3s;
  font-size: 24px !important;
}

.notification-trigger i {
  font-size: 24px !important;
}

.notification-trigger:hover {
  color: #366AFF;
  background-color: #f0f9ff;
}

.notification-trigger:focus {
  outline: none;
}

/* Badge样式调整 - 使用深度选择器 */
/deep/ .notification-badge .el-badge__content {
  background-color: #f56c6c !important;
  border-color: #f56c6c !important;
  color: white !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  min-width: 14px !important;
  height: 14px !important;
  line-height: 14px !important;
  border-radius: 7px !important;
  right: -39px !important;
  top: -8px !important;
  position: absolute !important;
  transform: translateX(0) translateY(0) !important;
}

/* 额外的选择器确保生效 */
.notification-badge /deep/ .el-badge__content {
  right: -28px !important;
  top: -8px !important;
}

/deep/ .el-badge__content {
  right: -20px !important;
  top: -8px !important;
}

/* 尝试使用transform来调整位置 */
/deep/ .notification-badge .el-badge__content {
  transform: translateX(24px) translateY(8px) !important;
}

.notification-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.notification-tabs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  border-bottom: 1px solid #EBEEF5;
  background-color: #F5F7FA;
}

.notification-tabs-header .el-tabs {
  flex: 1;
  height: auto;
}

.notification-tabs-header .el-tabs__header {
  margin: 0 !important;
  padding: 0;
}

.notification-tabs-header .el-tabs__nav-wrap {
  padding: 0;
}

.tabs-actions {
  display: flex;
  align-items: center;
  margin-left: 16px;
}

.tabs-content {
  flex: 1;
  overflow: hidden;
}
.notification-list{
  height: calc(100vh - 145px);
  overflow-y: auto;
}
.todo-list{
  height: calc(100vh - 145px);
  overflow-y: auto;
  padding: 0 12px;
}

.tab-badge {
  margin-left: 8px;
}

/* 覆盖Element UI的drawer样式 */
/deep/.el-drawer__body {
  padding: 0;
  height: 100%;
}

.el-tabs {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.el-tabs__content {
  flex: 1;
  overflow: hidden;
}
/deep/ .el-tabs__header{
  padding: 0 12px;
  margin: 0 12px !important;
}

/* 调试样式 */
/* .notification-center {
  z-index: 9999;
} */

.el-drawer {
  z-index: 2000 !important;
}

/* 确保弹窗显示在抽屉之上 */
/deep/ .el-message-box__wrapper {
  z-index: 3000 !important;
}

/deep/ .el-message-box {
  z-index: 9999 !important;
}
.el-drawer__header{
  background: #f6f8fd;
  display: flex;
  align-items: center;
}
/deep/ .el-message-box__wrapper{
  z-index: 9999 !important;
}

/* 针对自定义弹窗的样式 */
/deep/ .notification-confirm-dialog {
  z-index: 9999 !important;
}

/deep/ .notification-confirm-dialog .el-message-box__wrapper {
  z-index: 9999 !important;
}

/* 全局弹窗样式覆盖 */
body /deep/ .el-message-box__wrapper {
  z-index: 9999 !important;
}

body /deep/ .el-message-box {
  z-index: 9999 !important;
}

/* 调整Element UI通知的z-index和样式 */
/deep/ .el-notification {
  z-index: 10000 !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 20px 0 rgba(54, 106, 255, 0.15) !important;
  border: 1px solid #366AFF !important;
  animation-duration: 0.5s !important;
  animation-fill-mode: both !important;
}

/* 调整通知的进入和离开动画时间 */
/deep/ .el-notification.right-enter-active {
  transition: all 0.5s ease !important;
}

/deep/ .el-notification.right-leave-active {
  transition: all 0.8s ease !important;
}

/deep/ .el-notification.right-enter {
  transform: translateX(100%) !important;
}

/deep/ .el-notification.right-leave-to {
  transform: translateX(100%) !important;
}

/deep/ .el-notification .el-notification__icon {
  color: #366AFF !important;
  font-size: 20px !important;
}

/deep/ .el-notification .el-notification__title {
  color: #303133 !important;
  font-weight: 600 !important;
  font-size: 16px !important;
}

/deep/ .el-notification .el-notification__content {
  color: #606266 !important;
  font-size: 14px !important;
  text-align: left !important;
}

/deep/ .el-notification .el-notification__closeBtn {
  color: #909399 !important;
  font-size: 16px !important;
}

/deep/ .el-notification .el-notification__closeBtn:hover {
  color: #366AFF !important;
}

/* 调整Element UI Notify组件的样式 */
/deep/ .el-notification {
  z-index: 10000 !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 20px 0 rgba(54, 106, 255, 0.15) !important;
  border: 1px solid #366AFF !important;
  animation-duration: 0.5s !important;
  animation-fill-mode: both !important;
}

/* 调整通知的进入和离开动画时间 */
/deep/ .el-notification.right-enter-active {
  transition: all 0.5s ease !important;
}

/deep/ .el-notification.right-leave-active {
  transition: all 0.8s ease !important;
}

/deep/ .el-notification.right-enter {
  transform: translateX(100%) !important;
}

/deep/ .el-notification.right-leave-to {
  transform: translateX(100%) !important;
}

/deep/ .el-notification .el-notification__icon {
  color: #366AFF !important;
  font-size: 20px !important;
}

/deep/ .el-notification .el-notification__title {
  color: #303133 !important;
  font-weight: 600 !important;
  font-size: 16px !important;
}

/deep/ .el-notification .el-notification__content {
  color: #606266 !important;
  font-size: 14px !important;
  text-align: left !important;
}

/deep/ .el-notification .el-notification__closeBtn {
  color: #909399 !important;
  font-size: 16px !important;
}

/deep/ .el-notification .el-notification__closeBtn:hover {
  color: #366AFF !important;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .notification-center .el-drawer {
    width: 100% !important;
  }
}
</style>
